/**
 * Condo/PUD Warranty Form Types
 *
 * Used by the interactive warranty form in the Internal Conditions Portal.
 * Condition external ID: 0413
 */

import type { EncompassCondition } from './approval-letter';

// All 12 warranty type options
export type WarrantyType =
  // Condominium - Fannie Mae
  | 'condo_fannie_type_q'
  | 'condo_fannie_type_s'
  | 'condo_fannie_type_t'
  | 'condo_fannie_type_v'
  // Condominium - Freddie Mac
  | 'condo_freddie_full_new'
  | 'condo_freddie_full_established'
  | 'condo_freddie_full_2_4'
  | 'condo_freddie_streamline'
  // PUD - Fannie Mae
  | 'pud_fannie_type_e'
  | 'pud_fannie_type_f'
  // PUD - Freddie Mac
  | 'pud_freddie_established'
  | 'pud_freddie_new';

// Display configuration for each warranty type
export interface WarrantyTypeOption {
  value: WarrantyType;
  label: string;
  description: string;
}

export const WARRANTY_TYPE_OPTIONS: Record<string, WarrantyTypeOption[]> = {
  'Condominium - Fannie Mae': [
    {
      value: 'condo_fannie_type_q',
      label: 'Type Q',
      description: 'Limited review for established projects or established 2-4 unit projects.'
    },
    {
      value: 'condo_fannie_type_s',
      label: 'Type S',
      description: 'CPM expedited review or lender full review. Established projects including established 2-4 unit projects.'
    },
    {
      value: 'condo_fannie_type_t',
      label: 'Type T',
      description: 'Fannie Mae review. Any project accepted via PERS or with an unexpired final or conditional project acceptance status (1028).'
    },
    {
      value: 'condo_fannie_type_v',
      label: 'Type V',
      description: 'DU refi plus.'
    }
  ],
  'Condominium - Freddie Mac': [
    {
      value: 'condo_freddie_full_new',
      label: 'Full Review',
      description: 'New projects attached and detached.'
    },
    {
      value: 'condo_freddie_full_established',
      label: 'Full Review',
      description: 'Established projects attached and detached.'
    },
    {
      value: 'condo_freddie_full_2_4',
      label: 'Full Review',
      description: '2-4 unit Condominium project.'
    },
    {
      value: 'condo_freddie_streamline',
      label: 'Streamline Review',
      description: 'Established attached and detached projects.'
    }
  ],
  'PUD - Fannie Mae': [
    {
      value: 'pud_fannie_type_e',
      label: 'Type E',
      description: 'Established.'
    },
    {
      value: 'pud_fannie_type_f',
      label: 'Type F',
      description: 'New or proposed.'
    }
  ],
  'PUD - Freddie Mac': [
    {
      value: 'pud_freddie_established',
      label: 'Established Project',
      description: ''
    },
    {
      value: 'pud_freddie_new',
      label: 'New Project',
      description: ''
    }
  ]
};

// Bi-directional mapping: Encompass field 1012 values ↔ our WarrantyType keys
export const ENCOMPASS_TO_WARRANTY_MAP: Record<string, WarrantyType> = {
  Q_LimitedReviewEst: 'condo_fannie_type_q',
  S_ExpeditedEst: 'condo_fannie_type_s',
  T_FannieMaeReview: 'condo_fannie_type_t',
  V_NoReviewSiteCondo: 'condo_fannie_type_v',
  C_ICondominium: 'condo_freddie_full_new',
  B_IICondominium: 'condo_freddie_full_established',
  '2To4UnitProject': 'condo_freddie_full_2_4',
  A_IIICondominium: 'condo_freddie_streamline',
  E_PUD: 'pud_fannie_type_e',
  F_PUD: 'pud_fannie_type_f'
};

// Reverse map for Encompass write-back
export const WARRANTY_TO_ENCOMPASS_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(ENCOMPASS_TO_WARRANTY_MAP).map(([k, v]) => [v, k])
);

// Form data submitted by the user
export interface CondoPudWarrantyFormData {
  loanNumber: string;
  borrowerName: string;
  propertyAddress: string;
  projectName: string;
  phaseNumber: string;
  projectAddress: string;
  warrantyType: WarrantyType;
  cpmCertNumber?: string;
  isNonWarrantable: boolean;
  nonWarrantableReason: string;
  acceptingInvestor: string;
  signerName: string;
  signerTitle: string;
  signatureDataUrl: string;
  signedDate: string;
}

// Modal component props
export interface CondoPudWarrantyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  condition: EncompassCondition;
  loanId: string;
  loanData: {
    loanNumber: string;
    borrowerName: string;
    propertyAddress: string;
  };
  /** Pre-fill data from Encompass (fields 1012 & 3316 stored in encompass_loans) */
  encompassData?: {
    projectClassification?: string;
    isNonWarrantable?: boolean;
  };
  jwt: string;
  user: {
    fullName: string;
    title?: string;
  };
  onSuccess: () => void;
}
